import React, { useState } from "react";
import { supabase } from "../../lib/supabase";
import { X, User, Mail, Lock, Check } from "lucide-react";
import useWhitelistManager from "../../hooks/useWhitelistManager";
import { ADMIN_DOMAINS } from "../../utils/constants";

const AddUserModal = ({ onClose, onUserAdded }) => {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Use whitelist manager to add user to whitelist
  const { addToWhitelist } = useWhitelistManager();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const isAdminDomain = (email) => {
    const domain = email.split("@")[1];
    return ADMIN_DOMAINS.includes(domain);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");
    setSuccess("");

    try {
      console.log("🚀 Starting user creation process...");
      console.log("📝 Form data:", {
        username: formData.username,
        email: formData.email,
        password: "[HIDDEN]",
      });

      // Step 1: Check if username already exists
      console.log("🔍 Checking username availability...");
      const { data: existingUsername, error: usernameError } =
        await supabase
          .from("profiles")
          .select("username")
          .eq("username", formData.username)
          .single();

      if (usernameError && usernameError.code !== "PGRST116") {
        console.error("❌ Username check failed:", usernameError);
        throw new Error(`Username check failed: ${usernameError.message}`);
      }

      if (existingUsername) {
        console.log("❌ Username already exists:", formData.username);
        throw new Error(`Username "${formData.username}" already exists.`);
      }

      // Step 2: Check if email already exists
      console.log("📧 Checking email availability...");
      const { data: existingEmail, error: emailError } = await supabase
        .from("profiles")
        .select("email")
        .eq("email", formData.email)
        .single();

      if (emailError && emailError.code !== "PGRST116") {
        console.error("❌ Email check failed:", emailError);
        throw new Error(`Email check failed: ${emailError.message}`);
      }

      if (existingEmail) {
        console.log("❌ Email already exists:", formData.email);
        throw new Error(`Email "${formData.email}" already exists.`);
      }

      console.log(
        "✅ Username and email are available, proceeding with user creation..."
      );

      // Auto-verify ALL admin-created users
      const shouldAutoVerify = true;

      // Step 3 + 4: Create auth user + update profile via secure edge function
      // (service_role lives server-side; client no longer holds admin keys)
      console.log("🔐 Creating user via admin-user-management edge function...");
      const { data, error: invokeError } = await supabase.functions.invoke(
        "admin-user-management",
        {
          body: {
            action: "createUserWithProfile",
            email: formData.email,
            password: formData.password,
            username: formData.username,
            autoVerify: true,
            sendInvite: false,
          },
        }
      );

      // Transport/thrown error from the function invocation
      if (invokeError) {
        console.error("❌ User creation failed (transport):", invokeError);
        throw new Error(`Auth creation failed: ${invokeError.message}`);
      }

      // Handled domain error returned in the response body (HTTP 200 + { error })
      if (data?.error) {
        console.error("❌ User creation failed:", data.error);
        const { kind, message } = data.error;
        if (kind === "email_already_registered" || kind === "email_taken") {
          throw new Error(
            `Email "${formData.email}" is already registered in auth system.`
          );
        }
        if (kind === "username_taken") {
          throw new Error(`Username "${formData.username}" already exists.`);
        }
        if (kind === "profile_failed") {
          throw new Error(`Profile update failed: ${message}`);
        }
        throw new Error(message || "Failed to create user");
      }

      if (!data?.user) {
        throw new Error("Auth user creation returned no user data");
      }

      console.log("✅ User created successfully:", data.user.id);

      // Step 5: Add user to affiliate whitelist
      console.log("📋 Adding user to affiliate whitelist...");
      try {
        await addToWhitelist(formData.username, [], false); // Add with no patterns, no auto-reassign
        console.log("✅ User added to affiliate whitelist successfully!");
      } catch (whitelistError) {
        console.warn(
          "⚠️ Failed to add user to whitelist:",
          whitelistError.message
        );
        // Don't fail the entire process if whitelist addition fails
      }

      const successMessage = `User created successfully! Email auto-verified (admin created). Added to affiliate whitelist. Login: ${formData.email}`;

      setSuccess(successMessage);

      // Call callback to refresh user list
      if (onUserAdded) {
        onUserAdded({
          id: data.user.id,
          email: formData.email,
          username: formData.username,
          email_verified: shouldAutoVerify,
        });
      }

      // Clear form
      setFormData({
        username: "",
        email: "",
        password: "",
      });
    } catch (error) {
      console.error("Error creating user:", error);
      setError(error.message || "Failed to create user");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center z-[9999] p-4">
      {/* Glass Morphism Modal */}
      <div className="bg-white/10 backdrop-blur-2xl border border-white/20 rounded-3xl shadow-2xl w-full max-w-md relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5 rounded-3xl"></div>
        <div className="absolute -top-10 -right-10 w-20 h-20 bg-[#B8460E]/10 rounded-full blur-2xl animate-pulse"></div>
        <div
          className="absolute -bottom-10 -left-10 w-20 h-20 bg-blue-500/10 rounded-full blur-2xl animate-pulse"
          style={{ animationDelay: "2s" }}
        ></div>

        {/* Shine effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -skew-x-12 translate-x-[-200%] hover:translate-x-[200%] transition-transform duration-2000 pointer-events-none rounded-3xl"></div>

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10 backdrop-blur-sm relative z-10">
          <h2 className="text-xl font-bold text-white drop-shadow-lg font-display">
            Add New User
          </h2>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white hover:bg-white/10 transition-all duration-300 p-2 rounded-xl"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5 relative z-10">
          {/* Username Field */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">
              Username
            </label>
            <div className="relative">
              <User className="absolute left-3 top-3.5 h-4 w-4 text-white/60" />
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                className="w-full pl-10 pr-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-white/50 focus:ring-2 focus:ring-[#B8460E]/50 focus:border-[#B8460E]/50 transition-all duration-300"
                placeholder="Enter username"
                required
              />
            </div>
          </div>

          {/* Email Field */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-3.5 h-4 w-4 text-white/60" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full pl-10 pr-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-white/50 focus:ring-2 focus:ring-[#B8460E]/50 focus:border-[#B8460E]/50 transition-all duration-300"
                placeholder="Enter email address"
                required
              />
            </div>
            {formData.email && (
              <div className="mt-2 flex items-center text-sm text-green-300 bg-green-500/10 p-2 rounded-xl backdrop-blur-sm">
                <Check className="h-4 w-4 mr-2" />
                Admin created - email will be auto-verified
              </div>
            )}
          </div>

          {/* Password Field */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-3.5 h-4 w-4 text-white/60" />
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full pl-10 pr-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-white/50 focus:ring-2 focus:ring-[#B8460E]/50 focus:border-[#B8460E]/50 transition-all duration-300"
                placeholder="Enter password"
                required
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-300 p-3 rounded-xl backdrop-blur-sm">
              {error}
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="bg-green-500/10 border border-green-500/20 text-green-300 p-3 rounded-xl backdrop-blur-sm">
              {success}
            </div>
          )}

          {/* Form Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-xl hover:bg-white/20 transition-all duration-300 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-[#B8460E] to-[#8B3A0E] text-white rounded-xl hover:from-[#8B3A0E] hover:to-[#B8460E] transition-all duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                  Creating...
                </span>
              ) : (
                "Create User"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUserModal;
